import React, { useState, useRef } from 'react'
import { X, Upload, Trash2, Camera } from 'lucide-react'

interface AtualizarFotoPerfilModalProps {
  usuarioId: string
  fotoAtual?: string | null
  onClose: () => void
  onSuccess: (novaFotoUrl: string) => void
}

export default function AtualizarFotoPerfilModal({ 
  usuarioId, 
  fotoAtual, 
  onClose, 
  onSuccess 
}: AtualizarFotoPerfilModalProps) {
  const [preview, setPreview] = useState<string | null>(fotoAtual || null)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          
          // Redimensionar para máximo 800x800 mantendo proporção
          let width = img.width
          let height = img.height
          const maxSize = 800
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width
              width = maxSize
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height
              height = maxSize
            }
          }
          
          canvas.width = width
          canvas.height = height
          
          ctx?.drawImage(img, 0, 0, width, height)
          
          // Comprimir para JPEG com qualidade 0.8
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8)
          resolve(compressedBase64)
        }
        img.onerror = reject
        img.src = e.target?.result as string
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de arquivo não permitido. Use JPG, PNG ou WEBP')
      return
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Arquivo muito grande. Tamanho máximo: 5MB')
      return
    }

    setError(null)
    setArquivo(file)

    try {
      // Comprimir e criar preview
      const compressedBase64 = await compressImage(file)
      setPreview(compressedBase64)
    } catch (err) {
      console.error('Erro ao processar imagem:', err)
      setError('Erro ao processar imagem')
    }
  }

  const handleUpload = async () => {
    if (!arquivo || !preview) {
      setError('Selecione uma foto primeiro')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const response = await fetch('/api/gestao/usuario-foto-perfil', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          usuario_id: usuarioId,
          foto_base64: preview,
          file_name: arquivo.name,
          file_type: arquivo.type
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao fazer upload')
      }

      onSuccess(data.foto_url)
      onClose()
    } catch (err: any) {
      console.error('Erro ao fazer upload:', err)
      setError(err.message || 'Erro ao fazer upload da foto')
    } finally {
      setUploading(false)
    }
  }

  const handleRemoverFoto = async () => {
    if (!confirm('Deseja realmente remover sua foto de perfil?')) return

    setUploading(true)
    setError(null)

    try {
      const response = await fetch(`/api/gestao/usuario-foto-perfil?usuario_id=${usuarioId}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao remover foto')
      }

      setPreview(null)
      setArquivo(null)
      onSuccess('')
      onClose()
    } catch (err: any) {
      console.error('Erro ao remover foto:', err)
      setError(err.message || 'Erro ao remover foto')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Camera className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Atualizar Foto de Perfil</h2>
              <p className="text-sm text-white/90">JPG, PNG ou WEBP (máx. 5MB)</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Preview */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              {preview ? (
                <img
                  src={preview}
                  alt="Preview"
                  className="w-40 h-40 rounded-full object-cover border-4 border-orange-200"
                />
              ) : (
                <div className="w-40 h-40 rounded-full bg-gradient-to-r from-orange-400 to-red-400 flex items-center justify-center border-4 border-orange-200">
                  <Camera className="w-16 h-16 text-white" />
                </div>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-5 h-5" />
              <span className="font-medium">Selecionar Foto</span>
            </button>

            {arquivo && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="font-medium">Enviando...</span>
                  </>
                ) : (
                  <>
                    <Camera className="w-5 h-5" />
                    <span className="font-medium">Salvar Foto</span>
                  </>
                )}
              </button>
            )}

            {fotoAtual && (
              <button
                onClick={handleRemoverFoto}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-5 h-5" />
                <span className="font-medium">Remover Foto</span>
              </button>
            )}

            <button
              onClick={onClose}
              disabled={uploading}
              className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
